import {
    React,
    CustomDataGrid, CustomButton, CustomTextField,
    useState, useApi, useEffect, useMemo,
    Box, Dialog, DialogContent, DialogTitle, Divider,
    GridColDef, IconButton, CloseIcon,
    Typography, Toaster,
    formatCurrency,
} from '../../../utils/globalImports';
import { OutstandingRows } from '../../../interfaces/forecast.interface';
import { ApiResponse } from '../../../services/apiService';

interface ModalProps {
    open: boolean;
    onClose: (commentAdded: boolean) => void;
    data: OutstandingRows[];
    isBaselineComment?: boolean;
}

const RowCommentEditor = React.memo(({ rowId, initialValue, onChange }: {
    rowId: number,
    initialValue: string,
    onChange: (id: number, value: string) => void
}) => {
    const [localVal, setLocalVal] = useState(initialValue);

    useEffect(() => {
        setLocalVal(initialValue); // If data changes externally
    }, [initialValue]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        setLocalVal(newVal);
        onChange(rowId, newVal); // Call parent handler
    };

    return (
        <CustomTextField
            // label="Notes/Comments"
            name={`notes-${rowId}`}
            value={localVal}
            onChange={handleChange}
            isTextArea
            sx={{
                '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                        border: 'none', // 👈 removes the border
                    },
                },
            }}
        />
    );
});

const BulkOutstandingComment: React.FC<ModalProps> = ({ open, onClose, data,isBaselineComment }) => {
    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [rows, setRows] = useState(data);
    const [commentsMap, setCommentsMap] = useState(() =>
        Object.fromEntries(data.map((row) => [row.id, row.notes || '']))
    );
    const { execute, loading } = useApi();

    const userId = Number(localStorage.getItem("userId"));

    const handleNoteChange = (id: number, value: string) => {
        setCommentsMap(prev => {
            if (prev[id] === value) return prev;
            return { ...prev, [id]: value };
        });
    };

    const columns: GridColDef<(typeof rows)[number]>[] = [
        {
            field: 'feeStageReferenceNumber', headerName: 'Code', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'description', headerName: 'Description', flex: 2, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'quantity', headerName: 'Quantity', flex: 1, headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'cost', headerName: 'Cost', flex: 1, headerAlign: 'center',
            align: 'center',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'totalCost', headerName: 'Total cost', flex: 1, headerAlign: 'center',
            align: 'center',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'outstanding', headerName: 'Outstanding', flex: 1, headerAlign: 'center',
            align: 'center',
            renderCell: (params) => formatCurrency(params.value),
        },
        {
            field: 'actions', headerName: 'Action', flex: 3, headerAlign: 'center',
            align: 'center',
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params: any) => (
                <Box sx={{ width: '100%' }}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()} // Prevent DataGrid intercept
                >
                    <RowCommentEditor
                        rowId={params.id}
                        initialValue={commentsMap[params.id] || ""}
                        onChange={handleNoteChange}
                    />
                </Box>
            ),
        },

    ];

    const handleNoteSubmit = async () => {
        const missingComments = rows.filter(row => !commentsMap[row.id]?.trim());

        if (missingComments.length > 0) {
            setToast({
                open: true,
                message: "Please provide comments for all rows.",
                type: "error"
            });
            return;
        }

        const newNotes = rows.map((row) => ({
            feeStageId: row.id,
            commentText: commentsMap[row.id],
            createdBy: Number(userId),
            isBaselineComment:isBaselineComment??null
        }));

        const response: ApiResponse<any> = await execute('post', '/Project/Saveoutstanding-comments', newNotes);

        if (response?.error) {
            setToast({ open: true, message: response.error, type: "error" });
        } else {
            setToast({
                open: true,
                message: typeof response.data === "string" ? response.data : response.data.message || "Note added successfully",
                type: "success"
            });
            onClose(true); // Close modal on success
        }
    };

    return (
        <>
            {/* Modal Dialog */}
            <Dialog open={open}
                maxWidth="xl"
                fullScreen
                PaperProps={{
                    sx: {
                        width: '90vw',
                        height: '90vh',
                        margin: 0,
                        maxWidth: '90vw', // prevent MUI maxWidth constraints
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        position: 'relative',
                        color: '#1e66ab',
                        display: 'flex',
                        mb: 0,
                        fontWeight: 500,
                        fontSize: '24px',
                        pr: 5,
                    }}>

                    Add Comment to Feestage Outstanding

                    <CustomButton
                        capsule
                        disabled={loading}

                        sx={{
                            position: 'absolute',
                            right: 200,
                            top: 8,
                            textTransform: 'none',
                            whiteSpace: 'nowrap',
                            width: '15%',
                        }}
                        onClick={handleNoteSubmit}
                    >
                        Add Notes/Comments
                    </CustomButton>

                    <IconButton
                        color="inherit"
                        onClick={() => onClose(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider></Divider>

                <DialogContent>
                    <Box sx={{ flexGrow: 1 }}>
                        <CustomDataGrid
                            rows={rows}
                            columns={columns}
                        />
                    </Box>

                    <Toaster
                        open={toast.open}
                        message={toast.message}
                        type={toast.type as "success" | "error"}
                        onClose={() => setToast({ ...toast, open: false })}
                    />
                </DialogContent>
            </Dialog>
        </>
    );

}

export default BulkOutstandingComment;